import {
  Add,
  KeyboardArrowDown,
  MoreHorizOutlined,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddUserModal from "../modals/AddUserModal";
import UserInvitedModal from "../modals/UserInvitedModal";
import axios from "axios";
import UpdateUserModal from "../modals/updateUserModa";
import planData from "../plans.json";
import DeleteUserModal from "../modals/DeleteUserModal";
import { Edit2, Mail, Phone, Shield, SquarePen, Trash, User } from "lucide-react";
import { getForwardNumberDisplayLabel } from "../../utils/forwardNumberChannels";
import { useAssistantUiBridge } from "../../assistant";

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const assistantBridge = useAssistantUiBridge();
  const [openAddUserModal, setOpenAddUserModal] = useState(false);

  const [openUserInvitedModal, setOpenUserInvitedModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error_msg, setErrorMsg] = useState(null);

  const [planDetails, setPlanDetails] = useState(null);
  const check_subscription = async () => {
    try {
      setLoader(true);
      let sub = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          customer_id: JSON.parse(localStorage.getItem("organization"))
            .subscription_details.M.stripe_cust_id.S,
        }
      );

      setPlanDetails(planData[sub.data?.planDetails[0].plan_id]);
      return;
    } catch (error) {
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message);
      }
      setLoader(false);
    }
  };
  useEffect(() => {
    check_subscription();
  }, []);

  const [users, setUsers] = useState([]);

  const fetchUsers = async () => {
    try {
      setLoader(true);
      const org = JSON.parse(localStorage.getItem("organization"));
      const organization_id = org?.orgId?.S ?? org?.orgId;
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "get-org-users",
        { organization_id }
      );
      const list = response.data?.users ?? [];
      setUsers(list);
    } catch (error) {
      if (error.response?.data?.message) {
        setErrorMsg(error.response.data.message);
      }
      setUsers([]);
    } finally {
      setLoader(false);
    }
  };

  // Current user's role in this org (from localStorage; no API call needed)
  const currentUserType = (() => {
    try {
      const org = JSON.parse(localStorage.getItem("organization"));
      const user = JSON.parse(localStorage.getItem("user"));
      const entry = org?.users?.L?.find((u) => u?.M?.userId?.S === user?.userId?.S);
      return entry?.M?.type?.S ?? null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    fetchUsers();
  }, []);

  // Onboarding assistant: open Add User via ?addUser=1
  useEffect(() => {
    if (searchParams.get("addUser") !== "1") return;
    if (currentUserType && currentUserType !== "user") {
      setOpenAddUserModal(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("addUser");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, currentUserType]);

  useEffect(() => {
    if (openUserInvitedModal) {
      assistantBridge?.emit?.("user_invited", {});
    }
  }, [openUserInvitedModal, assistantBridge]);

  const isTwilanswerEmail = (email) =>
    typeof email === "string" && email.toLowerCase().endsWith("@twilanswer.com");
  const usersCountForLimit =
    users?.filter((u) => !isTwilanswerEmail(u?.email)).length ?? 0;
  const teamMembersLimit = planDetails?.team_members ?? 0;
  const atTeamLimit = teamMembersLimit > 0 && usersCountForLimit >= teamMembersLimit;

  return (
    <div className=" px-3 lg:px-4 py-4 lg:py-8">
  
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[14px] lg:text-[17px] font-bold mb-1">Users</div>
          <div className="text-[12px] lg:text-[14px] text-[#6B7280]  mb-5">
            Manage users in your organization and their access levels.
          </div>
        </div>

        <div>
          {loader ? (
            <CircularProgress
              size={"1.2rem"}
              color="primary"
              sx={{ animationDuration: "0.7s" }}
            />
          ) : atTeamLimit ? (
            <Button
              variant="contained"
              color="primary"
              sx={{
                py: { lg: "8px", xs: "5px" },
                px: "20px",
                borderRadius: "4px",
                fontSize: { lg: "14px", xs: "12px" },
                fontWeight: "bolder",
              }}
              onClick={() => navigate("/dashboard/settings/billing")}
            >
              <Add
                sx={{
                  fontSize: { lg: "20px", xs: "15px" },
                  position: "relative",
                  bottom: 1,
                }}
              />
              Upgrade Plan to Add More Users
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              sx={{
                py: { lg: "8px", xs: "5px" },
                px: "20px",
                borderRadius: "4px",
                fontSize: { lg: "14px", xs: "12px" },
                fontWeight: "bolder",
              }}
              onClick={() => setOpenAddUserModal(true)}
            >
              <Add
                sx={{
                  fontSize: { lg: "20px", xs: "15px" },
                  position: "relative",
                  bottom: 1,
                }}
              />
              Add User
            </Button>
          )}
        </div>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!error_msg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
      >
        <Alert onClose={() => setErrorMsg(null)} severity="error" sx={{ width: "100%" }}>
          {error_msg}
        </Alert>
      </Snackbar>
      {atTeamLimit && (
        <div className="text-[12px] lg:text-[14px] text-[#6B7280] mb-3">
          You've reached the team member limit for your plan (
          {usersCountForLimit}/{teamMembersLimit}). Upgrade to Add
          More Users.
        </div>
      )}
      {currentUserType && currentUserType !== "user" ? (
        <>
          <div className="flex justify-end">
            <AddUserModal
              users={users}
              setUsers={setUsers}
              open={openAddUserModal}
              setOpen={setOpenAddUserModal}
              setOpenUserInvitedModal={setOpenUserInvitedModal}
              planDetails={planDetails}
              fetchUsers={fetchUsers}
              loader={loader}
              setLoader={setLoader}
            />
            <UserInvitedModal
              open={openUserInvitedModal}
              setOpen={setOpenUserInvitedModal}
              email={users && users.length > 0 && users[users.length - 1].email}
            />
          </div>
          <div className="mt-4">
            <UserList
              error_msg={error_msg}
              setErrorMsg={setErrorMsg}
              users={users}
              loader={loader}
              setLoader={setLoader}
              setUsers={setUsers}
              fetchUsers={fetchUsers}
            />
          </div>
        </>
      ) : (
        <Alert color="warning">
          <div className=" text-black-600 text-[14px] ">
            You can't view this page in this organization because your type is
            user
          </div>
        </Alert>
      )}
    </div>
  );
};

const UserList = ({
  users,
  loader,
  setLoader,
  setUsers,
  fetchUsers,
  error_msg,
  setErrorMsg,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [anchorEl, setAnchorEl] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
const [selectedIndex, setSelectedIndex] = useState(null);
  const handleClick = (event, index) => {
    setAnchorEl((prev) => ({ ...prev, [index]: event.currentTarget }));
    setCurrentIndex(index); // Track the index of the clicked row
  };

  const handleClose = (index) => {
    setAnchorEl((prev) => ({ ...prev, [index]: null }));
    setCurrentIndex(0); // Reset current index
  };

  const removeUser = async (user_id) => {
    setLoader(true);
    setErrorMsg(null);

    let check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );

    if (check.status === 200) {
      const user___ = check.data.user;

      if (
        user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        try {
          let user_data = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "delete-user",
            {
              user_performing_action_id: JSON.parse(
                localStorage.getItem("user")
              ).userId.S,
              user_id,
              organization_id: JSON.parse(localStorage.getItem("organization"))
                .orgId.S,
            }
          );
          localStorage.setItem(
            "organization",
            JSON.stringify(user_data.data.updatedItem)
          );
          let related_org = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "delete-related-org",
            {
              user_id,
              organization_id: JSON.parse(localStorage.getItem("organization"))
                .orgId.S,
            }
          );

          fetchUsers();

          setLoader(false);
        } catch (error) {
          if (error.response && error.response.data) {
            setErrorMsg(error.response.data.message);
          }
          setLoader(false);
        }
      } else {
        window.location.reload();
      }
    }
  };

  const rows = [
    {
      name: "slack.png",
      email: "Slack",
      userType: "Main Slack",
      status: "pending",
    },
    {
      name: "slack.png",
      email: "Slack",
      userType: "Main Slack",
      status: "inactive",
    },
    {
      name: "slack.png",
      email: "Slack",
      userType: "Main Slack",
      status: "active",
    },
    // Add more rows here as needed
  ];

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedRows = [...users].sort((a, b) => {
    if (sortConfig.key) {
      const isAscending = sortConfig.direction === "asc";
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return isAscending ? -1 : 1;
      }
      if (aValue > bValue) {
        return isAscending ? 1 : -1;
      }
    }
    return 0;
  });

  const renderSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? (
        <ArrowDropUpIcon fontSize="small" />
      ) : (
        <ArrowDropDownIcon fontSize="small" />
      );
    }
    return <ArrowDropDownIcon fontSize="small" className="opacity-50" />; // default icon
  };

  const [openUpdateUserModal, setOpenUpdateUserModal] = useState(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const [snackMsg, setSnackMsg] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const channels =
    JSON.parse(localStorage.getItem("organization"))?.channels?.L || [];

  const ownerDeleteTooltip =
    "The organization owner cannot be removed. Transfer ownership to another user first, then you can remove this account.";

  return (
    <div>
       <Snackbar  anchorOrigin={{ vertical: "top", horizontal: "center" }} open={openSnackbar} onClose={() => setOpenSnackbar(false)} message={snackMsg} />
      <TableContainer className="w-full verflow-x-auto lg:overflow-hidden">
        <Table className="no-borders" style={{ minWidth: window.innerWidth >= 1024 ? "300px" : "1050px" }}>
          <TableHead>
            <TableRow className="bg-[#F9FAFB] border-b-[1px] ">
              <TableCell
                className="font-semibold cursor-pointer text-[#6B7280]"
                style={{ color: "#6B7280", fontWeight: 550 }}
              >
                Name
              </TableCell>
              <TableCell
                className="font-semibold cursor-pointer"
                style={{ color: "#6B7280", fontWeight: 550 }}
              >
                Email
              </TableCell>
              <TableCell
                className="font-semibold cursor-pointer"
                style={{ color: "#6B7280", fontWeight: 550 }}
              >
                User Type
              </TableCell>
              <TableCell
                className="font-semibold cursor-pointer"
                style={{ color: "#6B7280", fontWeight: 550 }}
              >
                Forwarding Number
              </TableCell>

              <TableCell
                className="font-semibold cursor-pointer  style={{color:'#6B7280', fontWeight:550}}"
                style={{ textAlign: "", border: "none" }}
              >
                Status
              </TableCell>
              <TableCell className="font-semibold cursor-pointer"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, index) => (
              <TableRow
                key={index}
                className={`bg-white ${index === sortedRows.length - 1 ? "" : "border-b"}`}
                sx={{ border: "" }}
              >
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <div className="bg-[#E0E7FF] rounded-full py-1 w-fit h-fit px-2">
                      {" "}
                      <User className="w-[17px] text-[#4F46E5]" />{" "}
                    </div>
                    <div> {row.name}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-1 ">
                    <div className="">
                      {" "}
                      <Mail className="w-[17px] text-[#ADB3BD] " />{" "}
                    </div>
                    <div> {row.email}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="capitalize">
                    {row.userType == "admin" || row.userType == 'owner' && (
                      <div className="text-[#991B1B] flex items-center font-semibold bg-[#FEE2E2] rounded-full w-fit pr-4 pl-3 text-[13px]">
                        {" "}
                        <Shield className="w-[18px] mr-2" /> Admin
                      </div>
                    )}

                    {row.userType == "user" && (
                      <div className="text-[#1E40AF] flex items-center font-semibold bg-[#DBEAFE] rounded-full w-fit pr-4 pl-3 text-[13px]">
                        {" "}
                        <Shield className="w-[18px] mr-2" /> User
                      </div>
                    )}

                    {row.userType == "viewer" && (
                      <div className="text-[#1F2937] flex items-center font-semibold bg-[#F3F4F6] rounded-full w-fit pr-4 pl-3 text-[13px]">
                        {" "}
                        <Shield className="w-[18px] mr-2" /> Viewer
                      </div>
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  {row.forwarding_number ? (
                    <div className="flex items-center space-x-1 text-[13px]">
                      <Phone className="w-[15px] text-[#ADB3BD] shrink-0" />
                      <span>{getForwardNumberDisplayLabel(row.forwarding_number, channels)}</span>
                    </div>
                  ) : (
                    <span className="text-[13px] text-gray-400">—</span>
                  )}
                </TableCell>

                <TableCell>
                  {row.status === "pending" && (
                    <div className="text-[#FF8F6B] bg-[rgba(255,143,107,0.1)] text-center w-fit px-4 rounded-[20px]">
                      Pending
                    </div>
                  )}

                  {row.status === "active" && (
                    <div className="text-[#5B93FF] bg-[rgba(91,147,255,0.1)] text-center  w-fit px-4 rounded-[20px]">
                      Active
                    </div>
                  )}

                  {row.status === "inactive" && (
                    <div className="text-[#FF8F6B] bg-[rgba(255,143,107,0.1)] text-center w-fit px-4 rounded-[20px]">
                      Inactive
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-3">
                    {/* --- EDIT ICON --- */}
                    <SquarePen
                      onClick={() => {
                        setOpenUpdateUserModal(true);
                        setCurrentIndex(index);
                        setSelectedUser(row);
                        setSelectedIndex(index);
                      }}
                      size={20}
                      className={`cursor-pointer text-[#4F46E5] ${
                        sortedRows.filter(
                          (e) =>
                            e.user_id ===
                            JSON.parse(localStorage.getItem("user")).userId.S
                        )[0]?.userType == "owner" && row.userType === "owner"
                          ? "hidden"
                          : "block"
                      }`}
                    />

                    {/* --- DELETE ICON --- */}
                    {row.userType === "owner" ? (
                      <Tooltip
                        title={ownerDeleteTooltip}
                        placement="top"
                        arrow
                      >
                        <span className="inline-flex">
                          <Trash
                            size={20}
                            className="text-gray-300 cursor-not-allowed"
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Trash
                        onClick={() => {
                          setOpenDeleteUserModal(true);
                          setCurrentIndex(index);
                          setSelectedUser(row);
                          setSelectedIndex(index);
                        }}
                        size={20}
                        className={`cursor-pointer text-[#DC2627] ${
                          sortedRows.filter(
                            (e) =>
                              e.user_id ===
                              JSON.parse(localStorage.getItem("user")).userId.S
                          )[0]?.userType == "owner" && row.userType === "owner"
                            ? "hidden"
                            : "block"
                        }`}
                      />
                    )}

                  
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            {/* Modals (keep them hidden in DOM like before) */}
            <UpdateUserModal
                      open={openUpdateUserModal}
                      setOpen={setOpenUpdateUserModal}
                      users={users}
                      setUsers={setUsers}
                      user_data={selectedUser}
                      fetchUsers={fetchUsers}
                      sortedRows={sortedRows}
                      snackMsg={snackMsg}
                      setSnackMsg={setSnackMsg}
                      setOpenSnackbar={setOpenSnackbar}
                    />


                    <DeleteUserModal
                      open={openDeleteUserModal}
                      setOpen={setOpenDeleteUserModal}
                      removeUser={removeUser}
                      user_id={selectedUser?.user_id}
                      user_name={selectedUser?.name}
                      user_email={selectedUser?.email}
                      index={selectedIndex}
                      closeMenu={() => {}}
                      snackMsg={snackMsg}
                      setSnackMsg={setSnackMsg}
                      setOpenSnackbar={setOpenSnackbar}
                    />
        </Table>
      </TableContainer>
    </div>
  );
};

export default Users;
